
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import ShowObjectDialog from '@/components/dialogs/ShowObjectDialog.vue';
import {readHasModuleAccess, readMenuForm, readQueryBuilder} from '@/store/main/getters';
import {commitAddNotification, commitSetQueryBuilder} from '@/store/main/mutations';
import PdfPreviewDialog from '@/components/dialogs/PdfPreviewDialog.vue';
import QueryBuilder from '@/components/extras/QueryFilterBuilder.vue';
import {EnumQueryBuilderType, EnumQueryOperators, EnumReturnMode, IQueryBuilder} from '@/interfaces/query-builder';
import SentEmailDialog from '@/components/dialogs/SentEmailDialog.vue';
import AlertDialogWrapper from '@/components/dialogs/AlertDialogWrapper.vue';
import AlertDialogFrame from '@/components/dialogs/AlertDialogFrame.vue';
import {getFormValuesHeaders} from '@/utils';
import FormValuePreview from '@/components/dialogs/FormValuePreview.vue';
import { ItemsPerPageOptions } from './consts';
import DisplayValue from '@/components/DisplayValue.vue';
import RendererPreviewContent from '@/components/forms/RendererPreviewContent.vue';

@Component({
  computed: {
    ItemsPerPageOptions() {
      return ItemsPerPageOptions;
    },
  },
  components: {
    DisplayValue,
    RendererPreviewContent,
    FormValuePreview,
    AlertDialogFrame,
    AlertDialogWrapper,
    SentEmailDialog,
    QueryBuilder,
    PdfPreviewDialog,
    ShowObjectDialog,
  },
})
export default class DynamicTableAsync extends Vue {

  private get expandableTable() {
    return this.expandableTableProp && this.headersRemained?.length > 0;
  }

  private get canSentEmailPermission() {
    if (this.canSentEmail) {
      return readHasModuleAccess(this.$store)({module: 'direct_mail', method: 'can_sent'});
    }
    return false;
  }

  private get formData() {
    const data = readMenuForm(this.$store)(+this.formId)?.form_setting?.form_data?.controls;
    // return getFormValuesHeaders(data).filter((e: any) => e?.type !== 'datePickerControl');
    return getFormValuesHeaders(data);
  }

  private get queryBuilder() {
    return readQueryBuilder(this.$store);
  }

  private get menuItems() {
    return [
      {divider: true},
      {
        icon: 'mdi-download-box-outline',
        title: this.$t('download_based_filter'),
        click: this.downloadBasedFilter(null),
      },
    ];
  }
  @Prop(Array) public dataset: any;
  @Prop(Array) public headers: any;
  @Prop(Array) public headersRemained;
  @Prop(String) public moduleName;
  @Prop(Boolean) public canSentEmail!: boolean;
  @Prop(Boolean) public canEditRows!: boolean;
  @Prop(Boolean) public expandableTableProp!: boolean;
  @Prop(Number) public numberOfPages;
  @Prop(Number) public totalItems;
  @Prop(Number) public formId;
  @Prop(Function) public readDataFromAPIFunction: any;

  public openCSDialog: boolean = false;
  public showFilter: boolean = false;
  private expanded: [] = [];
  private page: number = 0;

  private loading: boolean = true;
  private downloadLoading: boolean = false;
  private canEdit: boolean = true;
  private canDelete: boolean = true;
  private canPreview: boolean = true;
  private options: any = {};
  private search: string = '';

  private alertDialog: any = null;
  private alertHeader: any = '';
  private alertMessage: any = '';
  private searchTimer: number = 0;

  private selectedFieldSearch: any = null;

  private fieldSearchQueryBuilder: any = {} as IQueryBuilder;


  @Watch('$route')
  public watchRoute(val, oldValue) {
    if (val?.params?.id !== oldValue?.params?.id) {
      this.readDataFromAPI();
      this.loadMountedSettings();
    }
  }

  @Watch('options')
  public watchOptions(val, oldValue) {
    this.readDataFromAPI();
    // const hasOldValue = oldValue.hasOwnProperty('page')
    // if (hasOldValue) {
    //   this.readDataFromAPI();
    // }
    // this.loading = false;
  }


  @Watch('search')
  public watchSearch(val, oldValue) {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    if (val?.length > 2 && this.selectedFieldSearch) {
      this.searchTimer = setTimeout(() => {
        this.executeSearchQuery(val);
        // this.readDataFromAPI();
      }, 1500);
    } else if (val?.length === 0) {
      commitSetQueryBuilder(this.$store, null);
      this.readDataFromAPI();
    }
  }

  public async readDataFromAPI() {
    this.loading = true;
    const {page, itemsPerPage} = this.options;
    if (page === 0) {
      this.options.page = 1;
    }
    const pageNumber = page - 1;
    let skipPages = pageNumber > 0 ? pageNumber * itemsPerPage : 0;
    if (this.queryBuilder?.type === EnumQueryBuilderType.FIELD) {
      skipPages = 0;
      this.options.page = 1;
    }

    await this.readDataFromAPIFunction({
      skip: skipPages,
      limit: itemsPerPage > 0 ? itemsPerPage : this.totalItems,
      query_builder: this.queryBuilder,
    });
    this.loading = false;
  }

  public async mounted() {
    await this.loadMountedSettings();
  }

  public async loadMountedSettings() {
    this.alertDialog = this.$refs.alert_dialog;
    this.alertHeader = `${this.$t('crud.delete')} ${this.$t('selected_item')}?`;
    this.alertMessage = this.$t('infos.delete_alert');
    if (this.moduleName) {
      this.canEdit = (
          readHasModuleAccess(this.$store)({module: this.moduleName, method: 'update'}) ||
          readHasModuleAccess(this.$store)({module: this.moduleName, method: 'edit'})
      );
      this.canDelete = readHasModuleAccess(this.$store)({module: this.moduleName, method: 'delete'});
      this.canPreview = readHasModuleAccess(this.$store)({module: this.moduleName, method: 'read'});
    }

    this.fieldSearchQueryBuilder = {
      return_mode: EnumReturnMode.RM_ALL,
      type: EnumQueryBuilderType.FIELD,
      children: [
        {
          logical_operator: null,
          query: {
            query_value: null,
            operator: EnumQueryOperators.OP_CONTAINS,
            operand: {
              name: null,
              label: null,
            },
          },
        },
      ],
    } as IQueryBuilder;

    this.showFilter = false;
    this.selectedFieldSearch = null;
    this.search = '';
    this.options.page = 0;
    commitSetQueryBuilder(this.$store, null);
  }

  private selectedFieldType(fieldName: string) {
    if (this.formData) {
      const filtered = this.formData.find((e) => {
        return e.name === fieldName;
      });
      if (filtered) {
        return filtered.type;
      }
    }
    return null;

  }

  private executeSearchQuery(searchValue: string) {
    const fieldType = this.selectedFieldType(this.selectedFieldSearch);

    if (['datePickerControl', 'datetime'].includes(fieldType)) {
      const isValidDate = searchValue.length === 10 && searchValue.includes('/');
      if (!isValidDate) {
        commitAddNotification(
            this.$store,
            {content: 'Busca por data deve ser no formato DD/MM/AAAA', color: 'warning'},
        );
        return;
      }
      searchValue = searchValue.split('/').reverse().join('-');
    }

    this.fieldSearchQueryBuilder.children[0].query.operand.name = this.selectedFieldSearch;
    this.fieldSearchQueryBuilder.children[0].query.query_value = searchValue;
    commitSetQueryBuilder(this.$store, this.fieldSearchQueryBuilder);
    this.readDataFromAPI();
  }

  private isDate(key) {
    const formDateTypes = ['datePickerControl', 'datetime'];
    if (typeof key === 'string') {
      return formDateTypes.includes(key);
    }

    const arr = [...this.headers].concat(this.headersRemained);
    const fieldType = arr.find((e: any) => e?.value === key)?.type;
    return formDateTypes.includes(fieldType);
  }

  private formatDate(currentDate) {
    if (!currentDate || currentDate.length < 10) {
      return '-----';
    }
    if (currentDate.includes('/')) {
      return currentDate;
    }

    if (currentDate.length === 10) {
      const [year, month, day] = currentDate.split('-');
      return `${day}/${month}/${year}`;
    }

    return new Date(currentDate).toLocaleString('pt-BR');
  }

  private getFormFileID(item) {
    return item?.form_files_id;
  }

  private isFormFileValue(item) {
    return item && typeof item === 'object' && (item.type || item.name || item.upload_status || item.form_files_id);
  }

  private formatFormFile(item) {
    if (!item) {
      return null;
    }
    if (item.upload_status === 'pending') {
      return `${item.name || 'Arquivo'} (upload pendente)`;
    }
    if (item.form_files_id) {
      return item.name || 'Arquivo anexado';
    }
    return `${item.name || 'Arquivo'} (indisponível)`;
  }

  private labelName(key: string) {
    return this.headersRemained.find((e) => e.value === key)?.text;
  }

  private async searchFunction(query) {
    commitSetQueryBuilder(this.$store, query);
    this.options.page = 0;
    await this.readDataFromAPI();
  }

  private editFunction(item): void {
    if (this.headers) {
      const filtered = this.headers.find((e) => {
        return e.value === 'action';
      });
      if (filtered && filtered.router_name !== null) {
        // this.$router.push({name: filtered.router_name, params: {id: item.id}});
        const routerToResolve = this.$router.resolve({name: filtered.router_name, params: {id: item.id}});
        window.open(routerToResolve.href, '_blank');
      } else {
        console.log('Opção indisponível');
      }
    }
  }

  private previewFunction(item): void {
    if (this.headers) {
      const filtered = this.headers.find((e) => {
        return e.value === 'action';
      });
      if (filtered?.preview_function) {
        const {route} = this.$router.resolve({name: filtered.preview_function, params: {id: item.id}});
        window.open(route.fullPath, '_blank');
      } else {
        console.log('Opção indisponível');
      }
    }
  }

  private async deleteFunction(item) {
    if (this.headers) {
      const filtered = this.headers.find((e) => e.delete_function);
      if (filtered) {
        const resp = await this.alertDialog.open({width: 450}).then((result) => {
          return result;
        });
        if (resp) {
          await filtered.delete_function(item.id);
          await this.readDataFromAPI();
        }
      } else {
        // @ts-ignore
        commitAddNotification(this.$store, {content: this.$t('notAllowed'), color: 'warning'});
      }
    }
  }

  private async downloadBasedFilter(qBuilder) {
    const queryBuilder = this.hasValidQueryBuilder(qBuilder) ? qBuilder : null;
    // The limit is negative because return all values based on filter
    this.downloadLoading = true;
    try {
      await this.readDataFromAPIFunction({
        skip: 0,
        limit: -1,
        query_builder: queryBuilder,
        is_downloaded: true,
        format: 'xlsx',
      });
    } finally {
      this.downloadLoading = false;
    }
  }

  private hasValidQueryBuilder(qBuilder) {
    if (!qBuilder?.children?.length) {
      return false;
    }
    return qBuilder.children.every((child) => {
      const query = child?.query;
      return Boolean(query?.operator && query?.operand?.name);
    });
  }

  private handleSearchTimer() {
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
    }
    this.searchTimer = setTimeout(() => {
      // this.readDataFromAPI();
    }, 1500);
  }
}
